import {
  Box,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
} from '@mui/material';
import { ReactNode } from 'react';

import {
  ChannelTypeChip,
  HsmStatusChip,
  ProviderAvatar,
} from './channel-visuals';

import { Channel } from '@/services/hsm-backend';

export interface ChannelRowProps {
  channel: Channel;
  /** Right-aligned action slot (link icon, "Dar de alta" button, info icon). */
  action?: ReactNode;
}

/**
 * One channel row: provider avatar, name + monospace id/phone, channelType
 * chip, HSM status chip and a trailing action slot. Shared by the read-only
 * channels card (Empresa tab) and the "Alta de línea" screen.
 *
 * Chips and the action render inline in a single flex `Stack` (not via MUI's
 * absolutely-positioned `secondaryAction`) so the action never overlaps the
 * chips.
 */
export const ChannelRow = ({ channel, action }: ChannelRowProps) => {
  const hasWhatsappNumber = Boolean(channel.whatsappNumber);

  return (
    <ListItem disableGutters sx={{ alignItems: 'center', gap: 1 }}>
      <ListItemAvatar>
        <ProviderAvatar
          provider={channel.provider ?? null}
          muted={!hasWhatsappNumber}
        />
      </ListItemAvatar>
      <ListItemText
        primary={channel.name}
        primaryTypographyProps={{ fontWeight: 600 }}
        secondary={
          <Box
            component="span"
            sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
          >
            {channel.channelId}
            {hasWhatsappNumber ? (
              ` · ${channel.phone}`
            ) : (
              <Box component="span" sx={{ color: 'text.disabled' }}>
                {' · Sin whatsappNumber'}
              </Box>
            )}
          </Box>
        }
      />
      <Stack direction="row" spacing={1} alignItems="center">
        <ChannelTypeChip channelType={channel.channelType} />
        {hasWhatsappNumber && (
          <HsmStatusChip status={channel.hsmLineStatus ?? null} />
        )}
        {action}
      </Stack>
    </ListItem>
  );
};
